import React, { useState, useEffect } from 'react';
import {
  getMember,
  updateMember,
  updateMemberActivity,
  SINGAPORE_DISTRICTS,
  MEMBER_EMPLOYMENT_STATUSES,
  MEMBER_INTERESTS,
} from '../../../api/communityApi';
import { saveSubscriptionPreferences } from '../../../api/pushTokenApi';
import { syncPushToken, getNotificationPermission } from '../../../api/fcmService';

// ──────────────────────────────────────────────────────────────────────────────
// Toast helper
// ──────────────────────────────────────────────────────────────────────────────
function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className={`bb-member-toast${type === 'error' ? ' error' : ''}`}>
      {message}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// MyProfileMember
// ──────────────────────────────────────────────────────────────────────────────
export default function MyProfileMember({ orgId, memberId, memberName, memberEmail, activeTab }) {
  const [member, setMember]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState(null);

  // Edit form state
  const [phone, setPhone]                   = useState('');
  const [address, setAddress]               = useState('');
  const [dateOfBirth, setDateOfBirth]       = useState('');
  const [district, setDistrict]             = useState('');
  const [employmentStatus, setEmployment]   = useState('');
  const [interests, setInterests]           = useState([]);

  // Privacy state
  const [showInDirectory, setShowInDirectory] = useState(false);
  const [showEmail, setShowEmail]             = useState(false);
  const [privacySaving, setPrivacySaving]     = useState(false);

  // Subscription preferences state
  const [subEmail, setSubEmail]           = useState(true);
  const [subPush, setSubPush]             = useState(true);
  const [subWhatsapp, setSubWhatsapp]     = useState(true);
  const [subDirectMail, setSubDirectMail] = useState(false);
  const [subSaving, setSubSaving]         = useState(false);

  const showToast = (message, type = 'success') => setToast({ message, type });

  // Load member on mount
  useEffect(() => {
    if (!orgId || !memberId) return;
    const load = async () => {
      try {
        const m = await getMember(orgId, memberId);
        if (m) {
          setMember(m);
          // Seed edit form
          setPhone(m.phone || '');
          setAddress(m.address || '');
          setDateOfBirth(m.dateOfBirth || '');
          setDistrict(m.district || '');
          setEmployment(m.employmentStatus || '');
          setInterests(m.interests || []);
          // Seed privacy
          setShowInDirectory(m.privacySettings?.showInDirectory ?? false);
          setShowEmail(m.privacySettings?.showEmail ?? false);
          // Seed subscription preferences (default email + push + whatsapp on for existing records)
          const sp = m.subscriptionPreferences || {};
          setSubEmail(sp.email !== undefined ? sp.email : true);
          setSubPush(sp.pushNotifications !== undefined ? sp.pushNotifications : true);
          setSubWhatsapp(sp.whatsApp !== undefined ? sp.whatsApp : (m.whatsappOptIn !== false));
          setSubDirectMail(sp.directMail !== undefined ? sp.directMail : false);
        }
      } catch (err) {
        console.error('Error loading member profile:', err);
        showToast('Failed to load profile', 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orgId, memberId]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleInterestToggle = (value) => {
    setInterests(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  const handleSaveProfile = async () => {
    if (!orgId || !memberId) return;
    setSaving(true);
    try {
      const data = { phone, address, dateOfBirth, district, employmentStatus, interests };
      await updateMember(orgId, memberId, data);
      await updateMemberActivity(orgId, memberId);
      // Refresh member
      const updated = await getMember(orgId, memberId);
      if (updated) setMember(updated);
      setEditing(false);
      showToast('Profile updated successfully');
    } catch (err) {
      console.error('Error saving profile:', err);
      showToast('Failed to save profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (member) {
      setPhone(member.phone || '');
      setAddress(member.address || '');
      setDateOfBirth(member.dateOfBirth || '');
      setDistrict(member.district || '');
      setEmployment(member.employmentStatus || '');
      setInterests(member.interests || []);
    }
    setEditing(false);
  };

  const handleSavePrivacy = async () => {
    if (!orgId || !memberId) return;
    setPrivacySaving(true);
    try {
      await updateMember(orgId, memberId, {
        privacySettings: { showInDirectory, showEmail },
      });
      showToast('Privacy settings updated');
    } catch (err) {
      console.error('Error saving privacy settings:', err);
      showToast('Failed to save privacy settings', 'error');
    } finally {
      setPrivacySaving(false);
    }
  };

  const handleSaveSubscriptions = async () => {
    if (!subEmail && !subPush && !subWhatsapp && !subDirectMail) {
      showToast('At least one communication channel must be selected.', 'error');
      return;
    }
    setSubSaving(true);
    try {
      const prefs = { email: subEmail, push: subPush, whatsapp: subWhatsapp, directMail: subDirectMail };
      await saveSubscriptionPreferences('member', prefs);
      if (subPush) {
        // Runs inside the Save click, so the browser can show its "Allow notifications?" dialog.
        const result = await syncPushToken('member', { prompt: true });
        if (result === 'saved') showToast('Preferences saved. Push notifications are on for this device.');
        else if (getNotificationPermission() === 'denied') showToast('Preferences saved, but notifications are blocked in this browser. Allow them in the browser\'s site settings.', 'error');
        else showToast('Preferences saved, but push could not be set up on this device.', 'error');
      } else {
        showToast('Subscription preferences saved');
      }
    } catch (err) {
      console.error('Error saving subscription preferences:', err);
      showToast('Failed to save preferences', 'error');
    } finally {
      setSubSaving(false);
    }
  };

  // ── Render helpers ───────────────────────────────────────────────────────────

  const getEmploymentLabel = (value) =>
    MEMBER_EMPLOYMENT_STATUSES.find(s => s.value === value)?.label || value || '—';

  const getWidowStageLabel = (value) =>
    value === 'current' ? 'Current (0–3 years)' : value === 'past' ? 'Past (3+ years)' : value || '—';

  const calculateAge = (dob) => {
    if (!dob) return null;
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const formatDob = (dob) => {
    if (!dob) return '—';
    const d = new Date(dob);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bb-member-loading-screen">
        <div className="bb-loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="bb-member-main">
        <p style={{ color: '#6B7280', fontSize: 14 }}>Profile not found.</p>
      </div>
    );
  }

  const displayName = [member.firstName, member.lastName].filter(Boolean).join(' ') || memberName || '—';

  // ── Tab: My Details ──────────────────────────────────────────────────────────

  if (activeTab === 'My Details') {
    return (
      <div>
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}

        <h2 className="bb-member-section-title">My Profile</h2>
        <p className="bb-member-section-sub">View and update your personal information</p>

        {/* Personal Info Section */}
        <div className="bb-member-profile-section">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h3 style={{ margin: 0 }}>Personal Information</h3>
            {!editing && (
              <button
                className="bb-member-btn bb-member-btn-outline bb-member-btn-sm"
                onClick={() => setEditing(true)}
              >
                Edit
              </button>
            )}
          </div>

          {/* Read-only view */}
          {!editing && (
            <>
              <div className="bb-member-profile-field">
                <span className="bb-member-profile-field-label">Name</span>
                <span className="bb-member-profile-field-value">{displayName}</span>
              </div>
              <div className="bb-member-profile-field">
                <span className="bb-member-profile-field-label">Email</span>
                <span className="bb-member-profile-field-value">{member.email || memberEmail || '—'}</span>
              </div>
              <div className="bb-member-profile-field">
                <span className="bb-member-profile-field-label">Date of Birth</span>
                <span className="bb-member-profile-field-value">{formatDob(member.dateOfBirth)}</span>
              </div>
              <div className="bb-member-profile-field">
                <span className="bb-member-profile-field-label">Phone</span>
                <span className="bb-member-profile-field-value">{member.phone || '—'}</span>
              </div>
              <div className="bb-member-profile-field">
                <span className="bb-member-profile-field-label">Address</span>
                <span className="bb-member-profile-field-value">{member.address || '—'}</span>
              </div>
              <div className="bb-member-profile-field">
                <span className="bb-member-profile-field-label">District</span>
                <span className="bb-member-profile-field-value">{member.district || '—'}</span>
              </div>
              <div className="bb-member-profile-field">
                <span className="bb-member-profile-field-label">Employment</span>
                <span className="bb-member-profile-field-value">{getEmploymentLabel(member.employmentStatus)}</span>
              </div>
              <div className="bb-member-profile-field">
                <span className="bb-member-profile-field-label">Widow Stage</span>
                <span className="bb-member-profile-field-value">{getWidowStageLabel(member.widowStage)}</span>
              </div>
              <div className="bb-member-profile-field">
                <span className="bb-member-profile-field-label">Interests</span>
                <span className="bb-member-profile-field-value">
                  {(member.interests || []).length === 0 ? (
                    <span style={{ color: '#9CA3AF' }}>None added yet</span>
                  ) : (
                    (member.interests || []).map(val => {
                      const label = MEMBER_INTERESTS.find(i => i.value === val)?.label || val;
                      return (
                        <span key={val} className="bb-member-interest-chip">{label}</span>
                      );
                    })
                  )}
                </span>
              </div>
            </>
          )}

          {/* Edit form */}
          {editing && (
            <div>
              {/* Name + Email are read-only even in edit mode */}
              <div className="bb-member-profile-field">
                <span className="bb-member-profile-field-label">Name</span>
                <span className="bb-member-profile-field-value" style={{ color: '#9CA3AF' }}>{displayName}</span>
              </div>
              <div className="bb-member-profile-field">
                <span className="bb-member-profile-field-label">Email</span>
                <span className="bb-member-profile-field-value" style={{ color: '#9CA3AF' }}>
                  {member.email || memberEmail || '—'}
                  <span style={{ fontSize: 11, marginLeft: 6, color: '#9CA3AF' }}>(cannot change)</span>
                </span>
              </div>

              {/* Date of Birth */}
              <div className="bb-member-profile-field" style={{ alignItems: 'center' }}>
                <span className="bb-member-profile-field-label">Date of Birth</span>
                <input
                  type="date"
                  value={dateOfBirth}
                  onChange={e => setDateOfBirth(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  style={{
                    flex: 1, padding: '6px 10px', border: '1px solid #D1D5DB',
                    borderRadius: 6, fontSize: 13,
                  }}
                />
              </div>

              {/* Phone */}
              <div className="bb-member-profile-field" style={{ alignItems: 'center' }}>
                <span className="bb-member-profile-field-label">Phone</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="e.g. +65 9123 4567"
                  style={{
                    flex: 1, padding: '6px 10px', border: '1px solid #D1D5DB',
                    borderRadius: 6, fontSize: 13,
                  }}
                />
              </div>

              {/* Address */}
              <div className="bb-member-profile-field" style={{ alignItems: 'center' }}>
                <span className="bb-member-profile-field-label">Address</span>
                <input
                  type="text"
                  value={address}
                  onChange={e => setAddress(e.target.value)}
                  placeholder="e.g. Block 123, Tampines Ave 1, #05-10, Singapore 521123"
                  style={{
                    flex: 1, padding: '6px 10px', border: '1px solid #D1D5DB',
                    borderRadius: 6, fontSize: 13,
                  }}
                />
              </div>

              {/* District */}
              <div className="bb-member-profile-field" style={{ alignItems: 'center' }}>
                <span className="bb-member-profile-field-label">District</span>
                <select
                  value={district}
                  onChange={e => setDistrict(e.target.value)}
                  style={{
                    flex: 1, padding: '6px 10px', border: '1px solid #D1D5DB',
                    borderRadius: 6, fontSize: 13,
                  }}
                >
                  <option value="">Select district…</option>
                  {SINGAPORE_DISTRICTS.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              {/* Employment Status */}
              <div className="bb-member-profile-field" style={{ alignItems: 'center' }}>
                <span className="bb-member-profile-field-label">Employment</span>
                <select
                  value={employmentStatus}
                  onChange={e => setEmployment(e.target.value)}
                  style={{
                    flex: 1, padding: '6px 10px', border: '1px solid #D1D5DB',
                    borderRadius: 6, fontSize: 13,
                  }}
                >
                  <option value="">Select status…</option>
                  {MEMBER_EMPLOYMENT_STATUSES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              {/* Interests multi-select */}
              <div className="bb-member-profile-field" style={{ alignItems: 'flex-start' }}>
                <span className="bb-member-profile-field-label" style={{ paddingTop: 4 }}>Interests</span>
                <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {MEMBER_INTERESTS.map(interest => (
                    <label
                      key={interest.value}
                      className="bb-member-q-option"
                      style={{ cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={interests.includes(interest.value)}
                        onChange={() => handleInterestToggle(interest.value)}
                      />
                      {interest.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button
                  className="bb-member-btn bb-member-btn-primary"
                  onClick={handleSaveProfile}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
                <button
                  className="bb-member-btn bb-member-btn-outline"
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Tab: Privacy Settings ────────────────────────────────────────────────────

  if (activeTab === 'Privacy Settings') {
    return (
      <div>
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}

        <h2 className="bb-member-section-title">My Profile</h2>
        <p className="bb-member-section-sub">Control how your information is shared within the community</p>

        {/* Informational box */}
        <div className="bb-member-info-box">
          <strong>What data do we store?</strong>
          <p style={{ margin: '8px 0 0' }}>
            We store your name, email, phone number, district, employment status, and interests to
            help match you with relevant programmes and connect you with other members. Your financial
            information and loss history are strictly accessible only to organisation administrators.
          </p>
          <p style={{ margin: '8px 0 0' }}>
            We do not share your information with third parties. Your data is used solely to provide
            support services and facilitate community connections through BespokeBridge.
          </p>
        </div>

        <div className="bb-member-profile-section">
          <h3>Directory Visibility</h3>

          {/* Show in directory toggle */}
          <div className="bb-member-privacy-toggle">
            <div>
              <div className="bb-member-privacy-toggle-label">Show my profile in member directory</div>
              <div className="bb-member-privacy-toggle-desc">
                Other members can see your name, district, and interests
              </div>
            </div>
            <label className="bb-member-toggle-switch">
              <input
                type="checkbox"
                checked={showInDirectory}
                onChange={e => setShowInDirectory(e.target.checked)}
              />
              <span className="bb-member-toggle-slider"></span>
            </label>
          </div>

          {/* Show email toggle */}
          <div className="bb-member-privacy-toggle">
            <div>
              <div className="bb-member-privacy-toggle-label">Allow other members to see my email</div>
              <div className="bb-member-privacy-toggle-desc">
                Your email will appear on your directory card if enabled
              </div>
            </div>
            <label className="bb-member-toggle-switch">
              <input
                type="checkbox"
                checked={showEmail}
                onChange={e => setShowEmail(e.target.checked)}
              />
              <span className="bb-member-toggle-slider"></span>
            </label>
          </div>

          <div style={{ marginTop: 20 }}>
            <button
              className="bb-member-btn bb-member-btn-primary"
              onClick={handleSavePrivacy}
              disabled={privacySaving}
            >
              {privacySaving ? 'Saving…' : 'Save Privacy Settings'}
            </button>
          </div>

          <p className="bb-member-privacy-note" style={{ marginTop: 12 }}>
            Changes take effect immediately. If you hide your profile, you will no longer appear in
            the member directory visible to other members.
          </p>
        </div>
      </div>
    );
  }

  // ── Tab: Subscription Preferences ───────────────────────────────────────────

  if (activeTab === 'Subscription Preferences') {
    const noneSelected = !subEmail && !subPush && !subWhatsapp && !subDirectMail;
    const toggleStyle = {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 0', borderBottom: '1px solid #F3F4F6',
    };
    return (
      <div>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        <h2 className="bb-member-section-title">My Profile</h2>
        <p className="bb-member-section-sub">Choose how you'd like to receive communications from us</p>

        <div className="bb-member-info-box" style={{ borderColor: '#FCD34D', background: '#FFFBEB' }}>
          <strong>At least one channel is required.</strong>
          <p style={{ margin: '6px 0 0', fontSize: 13 }}>
            You must remain opted in to at least one communication channel so we can reach you with
            important updates and support information.
          </p>
        </div>

        <div className="bb-member-profile-section" style={{ marginTop: 16 }}>
          <h3 style={{ marginBottom: 4 }}>Communication Channels</h3>
          <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
            Select the channels through which you consent to receive communications.
          </p>

          {/* Email */}
          <div style={toggleStyle}>
            <div>
              <div className="bb-member-privacy-toggle-label">Email</div>
              <div className="bb-member-privacy-toggle-desc">Receive newsletters, updates and programme information via email</div>
            </div>
            <label className="bb-member-toggle-switch">
              <input type="checkbox" checked={subEmail} onChange={e => setSubEmail(e.target.checked)} />
              <span className="bb-member-toggle-slider"></span>
            </label>
          </div>

          {/* Push Notifications */}
          <div style={toggleStyle}>
            <div>
              <div className="bb-member-privacy-toggle-label">Push Notifications</div>
              <div className="bb-member-privacy-toggle-desc">Receive instant alerts on your device for events and announcements</div>
            </div>
            <label className="bb-member-toggle-switch">
              <input type="checkbox" checked={subPush} onChange={e => setSubPush(e.target.checked)} />
              <span className="bb-member-toggle-slider"></span>
            </label>
          </div>

          {/* WhatsApp */}
          <div style={toggleStyle}>
            <div>
              <div className="bb-member-privacy-toggle-label">WhatsApp</div>
              <div className="bb-member-privacy-toggle-desc">Receive messages and reminders via WhatsApp</div>
            </div>
            <label className="bb-member-toggle-switch">
              <input type="checkbox" checked={subWhatsapp} onChange={e => setSubWhatsapp(e.target.checked)} />
              <span className="bb-member-toggle-slider"></span>
            </label>
          </div>

          {/* Direct Mail */}
          <div style={{ ...toggleStyle, borderBottom: 'none' }}>
            <div>
              <div className="bb-member-privacy-toggle-label">Direct Mail</div>
              <div className="bb-member-privacy-toggle-desc">Receive physical letters and printed materials at your address</div>
            </div>
            <label className="bb-member-toggle-switch">
              <input type="checkbox" checked={subDirectMail} onChange={e => setSubDirectMail(e.target.checked)} />
              <span className="bb-member-toggle-slider"></span>
            </label>
          </div>

          {noneSelected && (
            <div style={{ marginTop: 12, padding: '8px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, fontSize: 13, color: '#B91C1C' }}>
              Please select at least one channel before saving.
            </div>
          )}

          <div style={{ marginTop: 20 }}>
            <button
              className="bb-member-btn bb-member-btn-primary"
              onClick={handleSaveSubscriptions}
              disabled={subSaving || noneSelected}
            >
              {subSaving ? 'Saving…' : 'Save Preferences'}
            </button>
          </div>

          <p className="bb-member-privacy-note" style={{ marginTop: 12 }}>
            Changes take effect immediately. Opting out of a channel means you will no longer receive
            that type of communication from BespokeBridge.
          </p>
        </div>
      </div>
    );
  }

  return null;
}
